<template>
  <f7-block
    v-if="visible"
    class="lic-card"
    :class="`lic-card--${variant}`"
  >
    <div class="lic-card__inner">
      <f7-icon
        :f7="icon"
        size="20"
        class="lic-card__icon"
      ></f7-icon>
      <div class="lic-card__body">
        <p class="lic-card__text">{{ displayMessage }}</p>
        <f7-button
          v-if="ctaLabel && onCtaClick"
          small
          class="lic-card__cta"
          @click="onCtaClick"
        >
          {{ ctaLabel }}
        </f7-button>
      </div>
    </div>
  </f7-block>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  /**
   * The membership stateKey — drives variant, icon, and default message.
   * Accepted values: 'PENDING' | 'APPROVED_AWAITING_DEPOSIT' | 'BLOCKED' | 'REJECTED' | null
   */
  stateKey: {
    type: String,
    default: null,
  },
  /** Override the display message. Falls back to a default per stateKey. */
  message: {
    type: String,
    default: null,
  },
  /** Label for the optional action button. */
  ctaLabel: {
    type: String,
    default: null,
  },
  /** Click handler for the action button. Required when ctaLabel is set. */
  onCtaClick: {
    type: Function,
    default: null,
  },
});

const { t } = useI18n();

const VARIANT_MAP = {
  PENDING:                    'info',
  APPROVED_AWAITING_DEPOSIT:  'warning',
  BLOCKED:                    'danger',
  REJECTED:                   'muted',
};

const ICON_MAP = {
  PENDING:                    'clock_fill',
  APPROVED_AWAITING_DEPOSIT:  'exclamationmark_circle_fill',
  BLOCKED:                    'xmark_octagon_fill',
  REJECTED:                   'xmark_circle_fill',
};

const DEFAULT_MESSAGE_KEY_MAP = {
  PENDING:                   'wallet.lifecycle.pending.card',
  APPROVED_AWAITING_DEPOSIT: 'wallet.lifecycle.awaiting.card',
  BLOCKED:                   'wallet.lifecycle.blocked.card',
  REJECTED:                  'wallet.lifecycle.rejected.card',
};

const visible        = computed(() => props.stateKey != null && props.stateKey in VARIANT_MAP);
const variant        = computed(() => VARIANT_MAP[props.stateKey] ?? 'info');
const icon           = computed(() => ICON_MAP[props.stateKey]    ?? 'info_circle_fill');
const displayMessage = computed(() => {
  if (props.message) return props.message;
  const key = DEFAULT_MESSAGE_KEY_MAP[props.stateKey];
  return key ? t(key) : '';
});
</script>

<style scoped>
.lic-card {
  margin: 8px 16px;
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
}

.lic-card__inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
}

.lic-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lic-card__text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.lic-card__cta {
  align-self: flex-start;
}

/* Info (pending) */
.lic-card--info .lic-card__inner {
  background: rgba(59, 130, 246, 0.08);
  color: #1d4ed8;
}
.lic-card--info .lic-card__icon {
  color: #3b82f6;
}

/* Warning (awaiting deposit) */
.lic-card--warning .lic-card__inner {
  background: rgba(217, 119, 6, 0.08);
  color: #92400e;
}
.lic-card--warning .lic-card__icon {
  color: #d97706;
}

/* Danger (blocked) */
.lic-card--danger .lic-card__inner {
  background: rgba(220, 38, 38, 0.08);
  color: #991b1b;
}
.lic-card--danger .lic-card__icon {
  color: #dc2626;
}

/* Muted (rejected) */
.lic-card--muted .lic-card__inner {
  background: rgba(107, 114, 128, 0.08);
  color: #374151;
}
.lic-card--muted .lic-card__icon {
  color: #6b7280;
}
</style>
